import {DepositoryType, InvestmentType} from '@transactions/graphql';

/**
 * Account list filters, GitHub-PR-list style. Two independent dimensions:
 * - status: all / active / inactive / closed (`filter` URL search param).
 *   The param is absent for the contextual default (see
 *   parseExplicitAccountFilter): 'active' while browsing, 'all' while the
 *   user is narrowing by name or type. So closed and inactive accounts hide
 *   from the plain overview (inactive ones reachable via each section's
 *   "N inactive" disclosure) but always show up in an explicit lookup.
 * - type: any number of account types (`type` URL search param, comma-
 *   separated option keys). Empty means all types (param absent).
 */
export type AccountFilter = 'all' | 'active' | 'inactive' | 'closed';

/** Closed means a closing anchor is designated (see setAccountClosingAnchor). */
export const isAccountClosed = (account: {
  closingAnchorId?: string | null;
}): boolean => account.closingAnchorId != null;

/**
 * The balance-query slice the status filter reads. Undefined means the
 * balances are still streaming in — status is then unknown and the account
 * must be treated as active (rows never hide on partial data).
 */
export type ActivityInfo = {
  balance: number;
  hasRecentActivity?: boolean;
};

/**
 * Derived inactive state: open, exactly zero balance, and no raw activity in
 * the last year (server-derived `hasRecentActivity`). Derived, never stored
 * — the open-but-dormant accounts (churned cards, emptied brokerages,
 * settled gift cards) the list collapses by default. The zero must be exact:
 * a dormant account still holding value (dust, a forgotten credit) is
 * precisely what the list should keep surfacing.
 */
export const isAccountInactive = (
  account: {closingAnchorId?: string | null},
  activity: ActivityInfo | undefined,
): boolean =>
  !isAccountClosed(account) &&
  activity != null &&
  activity.balance === 0 &&
  activity.hasRecentActivity === false;

/** Minimal account shape the filters need — structurally satisfied by
 * AccountListItemFragment. */
export type FilterableAccount = {closingAnchorId?: string | null} & (
  | {__typename: 'DepositoryAccount'; depositoryType: DepositoryType}
  | {__typename: 'InvestmentAccount'; investmentType: InvestmentType}
);

/**
 * An explicit status choice, or null when the URL carries none (or garbage).
 * The distinction matters because the default is contextual: plain browsing
 * defaults to 'active' (the decluttered overview), but a view the user is
 * explicitly narrowing — name search, type filter — defaults to 'all'.
 * Hiding statuses is a browsing nicety; applied to an explicit lookup it
 * makes matching accounts vanish (worse: a dormant account shows until the
 * balance data that proves it inactive streams in, then drops out from
 * under the search). 'active' parses too, so an explicit Active pick while
 * narrowed survives in the URL.
 */
export const parseExplicitAccountFilter = (
  value: string | null,
): AccountFilter | null =>
  value === 'all' ||
  value === 'active' ||
  value === 'closed' ||
  value === 'inactive'
    ? value
    : null;

export const parseAccountFilter = (value: string | null): AccountFilter =>
  parseExplicitAccountFilter(value) ?? 'active';

export const matchesAccountFilter = (
  filter: AccountFilter,
  account: {closingAnchorId?: string | null},
  activity?: ActivityInfo,
): boolean => {
  switch (filter) {
    case 'all':
      return true;
    case 'closed':
      return isAccountClosed(account);
    case 'inactive':
      return isAccountInactive(account, activity);
    case 'active':
      return (
        !isAccountClosed(account) && !isAccountInactive(account, activity)
      );
  }
};

/**
 * Account type filter: the selected option keys. Empty means no filter (all
 * types). Keys are namespaced (`dep:checking`, `inv:brokerage`) since both
 * roles have an 'other' subtype.
 */
export type AccountTypeFilter = string[];

export type AccountTypeOption = {
  key: string;
  label: string;
  /** Menu section the option belongs to. */
  group: 'Banking' | 'Investments';
  matches: (account: FilterableAccount) => boolean;
};

// [enum value, URL slug, display label]. Slugs are explicit (not derived from
// the generated enum values, which are SCREAMING_CASE) to keep URLs readable
// and stable across codegen changes.
const DEPOSITORY_TYPES: [DepositoryType, string, string][] = [
  [DepositoryType.Checking, 'checking', 'Checking'],
  [DepositoryType.Savings, 'savings', 'Savings'],
  [DepositoryType.CreditCard, 'credit_card', 'Credit Card'],
  [DepositoryType.Cash, 'cash', 'Cash'],
  [DepositoryType.Wallet, 'wallet', 'Wallet'],
  [DepositoryType.Loan, 'loan', 'Loan'],
  [DepositoryType.Ledger, 'ledger', 'Ledger'],
  [DepositoryType.Other, 'other', 'Other'],
];

const INVESTMENT_TYPES: [InvestmentType, string, string][] = [
  [InvestmentType.Brokerage, 'brokerage', 'Brokerage'],
  [InvestmentType['401K'], '401k', '401(k)'],
  [InvestmentType.Ira, 'ira', 'IRA'],
  [InvestmentType.RothIra, 'roth_ira', 'Roth IRA'],
  [InvestmentType.Hsa, 'hsa', 'HSA'],
  [InvestmentType.Education, 'education', '529 plan'],
  [InvestmentType.Other, 'other', 'Other'],
];

export const ACCOUNT_TYPE_OPTIONS: AccountTypeOption[] = [
  ...DEPOSITORY_TYPES.map(
    ([type, slug, label]): AccountTypeOption => ({
      key: `dep:${slug}`,
      label,
      group: 'Banking',
      matches: (account) =>
        account.__typename === 'DepositoryAccount' &&
        account.depositoryType === type,
    }),
  ),
  ...INVESTMENT_TYPES.map(
    ([type, slug, label]): AccountTypeOption => ({
      key: `inv:${slug}`,
      label,
      group: 'Investments',
      matches: (account) =>
        account.__typename === 'InvestmentAccount' &&
        account.investmentType === type,
    }),
  ),
];

const typeOptionByKey = new Map(
  ACCOUNT_TYPE_OPTIONS.map((option) => [option.key, option]),
);

export const parseAccountTypeFilter = (
  value: string | null,
): AccountTypeFilter =>
  (value ?? '')
    .split(',')
    .filter((key) => typeOptionByKey.has(key))
    // Dedupe while preserving order (a hand-edited URL may repeat keys)
    .filter((key, index, keys) => keys.indexOf(key) === index);

export const serializeAccountTypeFilter = (
  filter: AccountTypeFilter,
): string | null => (filter.length === 0 ? null : filter.join(','));

export const matchesAccountTypeFilter = (
  filter: AccountTypeFilter,
  account: FilterableAccount,
): boolean =>
  filter.length === 0 ||
  filter.some((key) => typeOptionByKey.get(key)?.matches(account) ?? false);

/**
 * Overview sections. Each depository type gets its own section (reusing the
 * filter option keys, so persisted section order stays valid), while ALL
 * investment accounts share a single 'investments' section — on a balance
 * sheet the question is "how much is in investments", not "Roth vs
 * traditional"; /investments keeps the per-type breakdown. The Type filter
 * stays per-type (ACCOUNT_TYPE_OPTIONS above): sections group, options
 * filter.
 */
export type AccountSectionSpec = {
  /** Persisted in Settings.accountTypeOrder; also the collapse-state key. */
  key: string;
  label: string;
  matches: (account: FilterableAccount) => boolean;
};

export const ACCOUNT_SECTIONS: AccountSectionSpec[] = [
  ...DEPOSITORY_TYPES.map(
    ([type, slug, label]): AccountSectionSpec => ({
      key: `dep:${slug}`,
      label,
      matches: (account) =>
        account.__typename === 'DepositoryAccount' &&
        account.depositoryType === type,
    }),
  ),
  {
    key: 'investments',
    label: 'Investments',
    matches: (account) => account.__typename === 'InvestmentAccount',
  },
];

const sectionByKey = new Map(
  ACCOUNT_SECTIONS.map((section) => [section.key, section]),
);

/**
 * ACCOUNT_SECTIONS reordered by a persisted key order: listed keys first
 * (in that order), then the remaining sections in default order. Unknown
 * keys in the persisted list are ignored (this is also what retires the
 * pre-merge `inv:*` section keys from old persisted orders).
 */
export const orderSections = (
  persistedOrder: string[],
): AccountSectionSpec[] => {
  const listed = persistedOrder.flatMap((key) => {
    const section = sectionByKey.get(key);
    return section ? [section] : [];
  });
  const listedKeys = new Set(listed.map((section) => section.key));
  return [
    ...listed,
    ...ACCOUNT_SECTIONS.filter((section) => !listedKeys.has(section.key)),
  ];
};
